// Framer Motion (the `motion` package) shared helpers. Bridges the
// admin anim-speed slider to Motion's `transition.duration`, and holds
// the variants used for the special-case animations (spell fizzle,
// trap flip, combat lunge, play hover beat).
//
// Card-to-card zone moves (hand→battle, draw, bounce, die) are handled
// by Motion's `layoutId` feature, not by a variant in this file: every
// site that renders a card wraps it in `motion.div layoutId={card-${eid}}`,
// and Motion FLIP-animates between the old and new rects on its own.

import type { Transition } from "motion/react";
import { effectiveDurationMs } from "./animSpeed";

// Base wall-clock durations in milliseconds. Kept here as a single
// source of truth so tweaks land in one place.
export const DURATIONS = {
  /** Card flying between zones — hand↔battle, draw, bounce, die. */
  zoneMove: 380,
  /** Spell fizzle (cyan dissolve) hand→graveyard exit. */
  spellFizzle: 520,
  /** Single half of the trap flip (out, then in — total ~640 ms). */
  trapFlipHalf: 320,
  /** Combat lunge full keyframe duration. */
  combatLunge: 560,
  /** Play hover beat for creatures / traps. */
  playHover: 250,
  /** Opponent-play hover beat. The viewer didn't choose this card, so
   *  it dwells a clear half-second before the cast animation (spell
   *  fizzle / zone move) so the card is readable. Own plays stay snappy
   *  on the shorter per-type beats above. */
  oppPlayHover: 500,
  /** Trap-specific hover beat (slightly longer so the user reads it). */
  trapHover: 350,
  /** Half of `playHover` — spells start dissolving mid-beat. */
  spellHover: 125,
  /** Keyword-strip fade in (matches the CSS keyframe). */
  keywordStrip: 560,
} as const;

const EASE = [0.2, 0.9, 0.2, 1] as const;
const FLIP_EASE = [0.4, 0.0, 0.6, 1] as const;

/** Wall-clock seconds for a base ms value, scaled by the admin anim
 *  speed. Motion takes durations in seconds, not ms. */
export function secondsFor(baseMs: number): number {
  return effectiveDurationMs(baseMs) / 1000;
}

/** Default transition for layout-driven card moves between zones. */
export function zoneMoveTransition(): Transition {
  return {
    duration: secondsFor(DURATIONS.zoneMove),
    ease: [...EASE],
  };
}

/** Variants for a creature / trap entering battle via the play hover
 *  clone: a small pop at the release rect before the layout animation
 *  carries the card to its slot. */
export const playHoverVariants = {
  initial: { scale: 0.96, opacity: 0.85 },
  visible: { scale: 1, opacity: 1 },
};

/** Variants for a spell dissolving in place. grayscale + sepia + hue
 *  rotate to cobalt cyan reproduces the CSS keyframe we used to ship
 *  as `doi-spell-fizzle`. */
export const spellFizzleVariants = {
  initial: {
    opacity: 1,
    filter: "grayscale(0) sepia(0) hue-rotate(0deg) saturate(1) brightness(1)",
  },
  fizzling: {
    opacity: 0,
    filter:
      "grayscale(1) sepia(1) hue-rotate(180deg) saturate(3) brightness(1.5)",
  },
};

export function spellFizzleTransition(): Transition {
  return {
    duration: secondsFor(DURATIONS.spellFizzle),
    ease: [...EASE],
  };
}

/** Trap-flip variants. Runs a single 640 ms two-leg rotation
 *  (face-down 0→90 deg, face-up -90→0 deg) by chaining keyframes;
 *  `times` distributes them across the duration. */
export const trapFlipVariants = {
  faceDown: { rotateY: 0, opacity: 1 },
  flipping: {
    rotateY: [0, 90, -90, 0],
    opacity: [1, 0, 0, 1],
  },
};

export function trapFlipTransition(): Transition {
  return {
    duration: secondsFor(DURATIONS.trapFlipHalf * 2),
    ease: [...FLIP_EASE],
    times: [0, 0.5, 0.5, 1],
  };
}

/** Combat lunge variants. Up = the viewer's seat lunging toward the
 *  central seam (negative translateY); Down = the opponent's seat. */
export function combatLungeKeyframes(direction: "up" | "down") {
  const sign = direction === "up" ? -1 : 1;
  return {
    y: [0, -12 * sign, 60 * sign, 0],
    scale: [1, 1, 1.1, 1],
    rotate: [0, 0, -5 * sign, 0],
  };
}

export function combatLungeTransition(): Transition {
  return {
    duration: secondsFor(DURATIONS.combatLunge),
    ease: [...EASE],
    times: [0, 0.15, 0.45, 1],
  };
}
